import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api, type CurrencySet, type Me } from "./api.ts";
import { useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button } from "./souvenir.ts";

// My currency, in the profile's first card (DESIGN.md section 3, the person, and "The profile in groups";
// features/profile.md, "My currency" and "One for the account"): every ISO currency, the common ones first, found by
// its code or its name; my account's, one for all my trips, so every trip's Money, those I join or start later too,
// estimates in it at once, but where my AI set another for one trip alone. Picking one saves it; Undo puts back the
// one before.

// The ones most travellers here pay in, first; then every other, by name.
const COMMON = [
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "SGD",
  "AUD",
  "CAD",
  "NZD",
  "CHF",
  "CNY",
  "HKD",
  "KRW",
  "TWD",
  "THB",
  "MYR",
  "IDR",
  "PHP",
  "VND",
  "INR",
  "AED",
];

const allCodes = (): string[] => {
  try {
    return (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf("currency");
  } catch {
    return COMMON;
  }
};
let names: Intl.DisplayNames | null = null;
// A currency's name, as people say it: "Singapore Dollar".
export function currencyName(code: string): string {
  try {
    names ??= new Intl.DisplayNames(["en"], { type: "currency" });
    return names.of(code) ?? code;
  } catch {
    return code;
  }
}

export function MyCurrency({ me, onChanged }: { me: Me; onChanged: () => void }) {
  const id = useId();
  const online = useOnline();
  const stored = me.currency ?? null;
  // What I just saved, shown until the profile loads with it, and the one it replaced, for Undo.
  const [saved, setSaved] = useState<CurrencySet | null>(null);
  const [undone, setUndone] = useState<string | null>(null);
  const shown = undone !== null ? undone || null : (saved?.currency ?? stored);
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const search = useRef<HTMLInputElement>(null);
  const change = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) search.current?.focus();
  }, [open]);

  const codes = useMemo(() => {
    const all = allCodes();
    const rest = all.filter((c) => !COMMON.includes(c)).sort((a, b) => currencyName(a).localeCompare(currencyName(b)));
    return { common: COMMON.filter((c) => all.includes(c)), rest };
  }, []);
  const q = find.trim().toLowerCase();
  const matches = (c: string) => !q || c.toLowerCase().includes(q) || currencyName(c).toLowerCase().includes(q);
  const common = codes.common.filter(matches);
  const rest = codes.rest.filter(matches);

  const close = () => {
    setOpen(false);
    setFind("");
    requestAnimationFrame(() => change.current?.querySelector<HTMLElement>(".sv-btn")?.focus());
  };
  const pick = async (currency: string) => {
    if (busy) return;
    if (currency === shown) return close();
    setBusy(true);
    setError(null);
    try {
      setSaved(await api.setCurrency(currency));
      setUndone(null);
      close();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save your currency. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const undo = async () => {
    if (!saved) return;
    setBusy(true);
    setError(null);
    try {
      await api.setCurrency(saved.was);
      setUndone(saved.was ?? "");
      setSaved(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't undo it. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const option = (c: string) => (
    <li key={c}>
      <button
        type="button"
        className={`tm-currency-option${c === shown ? " is-chosen" : ""}`}
        aria-pressed={c === shown}
        disabled={busy}
        onClick={() => pick(c)}
      >
        <span className="tm-currency-code">{c}</span>
        <span className="callout tm-currency-name">{currencyName(c)}</span>
      </button>
    </li>
  );
  return (
    <section className="tm-settings-part tm-currency" aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className="tm-settings-label">
        Your currency
      </h3>
      <div className="tm-currency-now" ref={change}>
        <p className="callout tm-currency-shown">
          {shown ? (
            <>
              <strong>{shown}</strong> <span className="tm-muted">{currencyName(shown)}</span>
            </>
          ) : (
            <span className="tm-muted">None chosen yet</span>
          )}
        </p>
        {!open && (
          <Button variant="secondary" size="sm" disabled={!online} onClick={() => setOpen(true)}>
            {shown ? "Change" : "Choose"}
          </Button>
        )}
      </div>
      {open && (
        <form
          className="tm-currency-picker"
          aria-label="Choose your currency"
          onSubmit={(e) => {
            // Enter in the search: the first it finds.
            e.preventDefault();
            const first = common[0] ?? rest[0];
            if (q && first) pick(first);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            e.stopPropagation();
            close();
          }}
        >
          <label className="tm-hidden" htmlFor={`${id}-find`}>
            Find a currency
          </label>
          <input
            ref={search}
            id={`${id}-find`}
            className="sv-field-input tm-currency-find"
            type="search"
            autoComplete="off"
            placeholder="Find a currency: yen, SGD, baht…"
            value={find}
            onChange={(e) => setFind(e.target.value)}
          />
          <fieldset className="tm-currency-list">
            <legend className="tm-hidden">Currencies</legend>
            {common.length > 0 && (
              <>
                {!q && <p className="eyebrow tm-muted tm-currency-group">Common</p>}
                <ul className="tm-currency-options">{common.map(option)}</ul>
              </>
            )}
            {rest.length > 0 && (
              <>
                {!q && <p className="eyebrow tm-muted tm-currency-group">Every other</p>}
                <ul className="tm-currency-options">{rest.map(option)}</ul>
              </>
            )}
            {common.length + rest.length === 0 && (
              <p className="callout tm-muted tm-currency-group">No currency is called "{find.trim()}".</p>
            )}
          </fieldset>
          <span className="tm-currency-actions">
            <Button variant="quiet" size="sm" className="tm-inline-quiet" onClick={close}>
              Cancel
            </Button>
          </span>
        </form>
      )}
      <p className="caption tm-muted tm-currency-about">
        Money in every trip of yours, now and to come, is estimated in it at the European Central Bank's daily rates
        (none for a currency they don't cover), unless your AI set another for that trip alone. Your AI sees it too.
      </p>
      {saved && (
        <div className="tm-currency-said" role="status">
          <p className="callout tm-currency-said-words">Your currency is {saved.currency} now.</p>
          <Button variant="quiet" size="sm" icon="replay" className="tm-inline-quiet" disabled={busy} onClick={undo}>
            Undo
          </Button>
        </div>
      )}
      {undone !== null && (
        <p className="callout" role="status">
          {undone ? `Back to ${undone}.` : "Back to no currency of your own."}
        </p>
      )}
      {error && (
        <p className="callout tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </section>
  );
}
